import React, { useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import FormField from '../../components/common/FormField';
import {
  LeadDTO,
  LeadFileClient,
  LeadFileDTO,
  NotesClient,
  NotesDTO,
  SearchRequestDTO_leadFileSearchRequestDTO,
  SearchRequestDTO_notesSearchRequestDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { getProvinces } from '../../hooks/useProvinces';
import { getProjectTypes } from '../../hooks/useProjectTypes';
import { getPropertyTypes } from '../../hooks/usePropertyTypes';
import { getLeadStatus } from '../../hooks/useLeadStatuses';
import Card from '../common/Card';
import { API_BASE_URL } from '../../config/config';
import axiosInstance from '../../services/axiosInstance';
import NotesTable from '../tables/NotesTable';
import FileUpload from '../common/FileUpload';
import LeadFileTable from '../tables/LeadFileTable';
import { confirmDelete } from '../../utils/sweetAlertUtils';

interface LeadFormProps {
  initialValues: LeadDTO;
  onSubmit: (
    data: LeadDTO,
    submitFiles: LeadFileDTO[],
    submitNotes: string[],
  ) => void;
  onCancel: () => void;
}

interface NotesStatus extends NotesDTO {
  status: boolean;
}

const LeadForm: React.FC<LeadFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<LeadDTO>(initialValues);
  const [submitFiles, setSubmitFiles] = useState<LeadFileDTO[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [notes, setNotes] = useState<NotesDTO[]>([]);
  const [submitNotes, setSubmitNotes] = useState<string[]>([]);
  const [noteData, setNoteData] = useState<NotesDTO>({} as NotesDTO);
  const [notesCurrentPage, setNotesCurrentPage] = useState(1);
  const [notesPageSize, setNotesPageSize] = useState(10);
  const [notesTotalPages, setNotesTotalPages] = useState(1);
  const [notesTotalRecords, setNotesTotalRecords] = useState(0);
  const [notesIsLoading, setNotesIsLoading] = useState(false);

  const [leadFiles, setLeadFiles] = useState<LeadFileDTO[]>([]);
  const [leadFilesCurrentPage, setLeadFilesCurrentPage] = useState(1);
  const [leadFilesPageSize, setLeadFilesPageSize] = useState(10);
  const [leadFilesTotalPages, setLeadFilesTotalPages] = useState(1);
  const [leadFilesTotalRecords, setLeadFilesTotalRecords] = useState(0);
  const [leadFilesIsLoading, setLeadFilesIsLoading] = useState(false);

  const { provinces } = getProvinces();
  const { projectTypes } = getProjectTypes();
  const { propertyTypes } = getPropertyTypes();
  const { leadStatuses } = getLeadStatus();

  const leadFileApiClient = new LeadFileClient(API_BASE_URL, axiosInstance);
  const notesApiClient = new NotesClient(API_BASE_URL, axiosInstance);

  const fetchLeadFiles = async (page: number, size: number, filters = {}) => {
    const searchRequest: SearchRequestDTO_leadFileSearchRequestDTO = {
      filters: {
        leadId: formData.id || '',
        ...filters,
      },
      page,
      pageSize: size,
      sortBy: 'FileName',
      sortDescending: false,
    };

    try {
      setLeadFilesIsLoading(true);
      const response = await leadFileApiClient.searchLeadFiles(searchRequest);
      const totalRecords = response.data?.totalCount ?? 0;

      setLeadFiles((response.data?.results as NotesDTO[]) || []);
      setLeadFilesTotalRecords(totalRecords);
      setLeadFilesTotalPages(Math.ceil(totalRecords / size));
    } catch (error) {
      console.error('Error fetching lead files:', error);
    } finally {
      setLeadFilesIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadFiles(leadFilesCurrentPage, leadFilesPageSize);
  }, [leadFilesCurrentPage, leadFilesPageSize]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = (e.target as HTMLInputElement).files;

    setIsSubmitting(true);
    try {
      if (fileList && fileList?.length > 0) {
        let fileArray = Array.from(fileList);

        await Promise.all(
          fileArray.map((file) =>
            leadFileApiClient
              .uploadLeadFile(file.name, file)
              .then((response) => {
                let leadFile: LeadFileDTO = {
                  friendlyName: file.name,
                  fileName: response.data,
                };
                setSubmitFiles((prev) => [...prev, leadFile]);
              }),
          ),
        );
      }
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileDownload = (leadFileId: string) => {
    leadFileApiClient.generateLeadFileSas(leadFileId || '').then((url) => {
      window.open(url.data);
    });
  };

  const handleFileDelete = async (leadFile: LeadFileDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    if (confirmed) {
      leadFileApiClient
        .deleteLeadFile(leadFile.id || '')
        .then(() =>
          setLeadFiles(leadFiles.filter((lf) => lf.id !== leadFile.id)),
        );
    }
  };

  const fetchNotes = async (page: number, size: number, filters = {}) => {
    const searchRequest: SearchRequestDTO_notesSearchRequestDTO = {
      filters: {
        leadId: formData.id || '',
        ...filters,
      },
      page,
      pageSize: size,
      sortBy: 'FirstName',
      sortDescending: false,
    };

    try {
      setNotesIsLoading(true);
      const response = await notesApiClient.searchNotess(searchRequest);
      const totalRecords = response.data?.totalCount ?? 0;

      const fetchedNotes: NotesDTO[] = response.data?.results || [];
      const notesStatus: NotesStatus[] = fetchedNotes.map((note) => ({
        ...note,
        status: true,
      }));

      setNotes(notesStatus);
      setNotesTotalRecords(totalRecords);
      setNotesTotalPages(Math.ceil(totalRecords / size));
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setNotesIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes(notesCurrentPage, notesPageSize);
  }, [notesCurrentPage, notesPageSize]);

  const handleNoteDelete = async (note: NotesDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    if (confirmed) {
      if (note.id) {
        notesApiClient
          .deleteNotes(note.id || '')
          .then(() => setNotes(notes.filter((n) => n.id !== note.id)));
      } else {
        setNotes(notes.filter((n) => n.text !== note.text));
        setSubmitNotes(submitNotes.filter((n) => n !== note.text));
      }
    }
  };

  const validateForm = () => {
    const formErrors: { [key: string]: string } = {};

    if (!formData.firstName?.trim())
      formErrors.firstName = 'First name is required.';
    if (!formData.lastName?.trim())
      formErrors.lastName = 'Last name is required.';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      formErrors.email = 'Valid email is required.';
    }

    if (!formData.phoneNumber)
      formErrors.phoneNumber = 'Valid phone number is required.';

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    onSubmit(formData, submitFiles, submitNotes); // Pass the updated data back to the parent
  };

  const handleNoteAdd = () => {
    setSubmitNotes((prev) => [...prev, noteData.text || '']);
    setNotes((prev) => [
      ...prev,
      { text: noteData.text, status: false } as NotesStatus,
    ]);
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <div className="flex mb-4">
        <Card heading="Personal Information" className="m-3">
          <div className="grid grid-cols-3 gap-x-6 gap-y-2">
            <FormField
              id="firstName"
              label="First Name"
              type="text"
              value={formData.firstName || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter First Name"
              required={true}
              error={errors.firstName}
            />
            <FormField
              id="lastName"
              label="Last Name"
              type="text"
              value={formData.lastName || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Last Name"
              required={true}
              error={errors.lastName}
            />
            <FormField
              id="email"
              label="Email"
              type="text"
              value={formData.email || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Email"
              required={true}
              error={errors.email}
            />
            <FormField
              id="phoneNumber"
              label="Phone Number"
              type="text"
              value={formData.phoneNumber || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Phone Number"
              required={true}
              error={errors.phoneNumber}
            />
            <FormField
              id="address"
              label="Address"
              type="text"
              value={formData.address || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Address"
              error={errors.address}
            />
            <FormField
              id="city"
              label="City"
              type="text"
              value={formData.city || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter City"
              error={errors.city}
            />
            <FormField
              id="provinceId"
              label="Province"
              type="dropdown"
              value={formData.provinceId || ''}
              onChange={(e) => handleChange(e, setFormData)}
              options={provinces.map((province) => ({
                value: province.id,
                label: province.name || '',
              }))}
              placeholder="Enter Province"
              error={errors.province}
            />
          </div>
        </Card>
        <Card heading="Project Information" className="m-3">
          <div className="grid grid-cols-3 gap-x-6 gap-y-2">
            <FormField
              id="leadStatusId"
              label="Lead Status"
              type="dropdown"
              value={formData.leadStatusId || ''}
              onChange={(e) => handleChange(e, setFormData)}
              options={leadStatuses.map((leadStatus) => ({
                value: leadStatus.id,
                label: leadStatus.name || '',
              }))}
              placeholder="Enter Lead Status"
              error={errors.leadStatusId}
            />
            <FormField
              id="companyName"
              label="Company Name"
              type="text"
              value={formData.companyName || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Company Name"
              error={errors.companyName}
            />
            <FormField
              id="projectTypeId"
              label="Project Type"
              type="dropdown"
              value={formData.projectTypeId || ''}
              onChange={(e) => handleChange(e, setFormData)}
              options={projectTypes.map((projectType) => ({
                value: projectType.id,
                label: projectType.name || '',
              }))}
              placeholder="Enter Project Type"
              error={errors.projectTypeId}
            />
            <FormField
              id="maintenanceSupport"
              label="Maintenance Support?"
              type="checkbox"
              value={formData.maintenanceSupport || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Maintenance Support"
            />
            <FormField
              id="propertyTypeId"
              label="Property Type"
              type="dropdown"
              value={formData.propertyTypeId || ''}
              onChange={(e) => handleChange(e, setFormData)}
              options={propertyTypes.map((propertyType) => ({
                value: propertyType.id,
                label: propertyType.name || '',
              }))}
              placeholder="Enter Property Type"
              error={errors.propertyTypeId}
            />
            <FormField
              id="propertySize"
              label="Property Size"
              type="text"
              value={formData.propertySize || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Property Size"
              error={errors.propertySize}
            />
            <FormField
              id="energyConsumptionKWh"
              label="Energy Consumption (KWh)"
              type="number"
              value={formData.energyConsumptionKWh || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Energy Consumption (KWh)"
              error={errors.energyConsumptionKWh}
            />
            <FormField
              id="budget"
              label="Budget"
              type="number"
              value={formData.budget || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Budget"
              error={errors.budget}
            />
            <FormField
              id="financingOption"
              label="Financing?"
              type="checkbox"
              value={formData.financingOption || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Financing Option"
            />
            <FormField
              id="projectTimeline"
              label="Project Timeline"
              type="date"
              value={formData.projectTimeline || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Project Timeline"
              error={errors.projectTimeline}
            />
          </div>
        </Card>
      </div>
      <Card heading="Upload Project Files" className="m-3">
        <FileUpload
          id="leadFiles"
          placeholderText="Upload or drag and drop lead files"
          onChange={handleFileUpload}
          supportedFormats="PDF, JPG, PNG, XLS"
          multiple={true}
        />
        <LeadFileTable
          data={leadFiles}
          handleDownload={(file) => handleFileDownload(file)}
          handleDelete={(file) => handleFileDelete(file)}
          currentPage={leadFilesCurrentPage}
          totalPages={leadFilesTotalPages}
          setCurrentPage={setLeadFilesCurrentPage}
          pageSize={leadFilesPageSize}
          setPageSize={setLeadFilesPageSize}
          isLoading={leadFilesIsLoading}
        />
      </Card>
      <Card heading="Project Information" className="m-3">
        <FormField
          id="projectDetails"
          label="Project Details"
          type="textarea"
          value={formData.projectDetails || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Project Details"
          error={errors.projectDetails}
        />
        <FormField
          id="environmentalImpactInterest"
          label="Environmental Impact Interest"
          type="textarea"
          value={formData.environmentalImpactInterest || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Environmental Impact Interest"
          error={errors.environmentalImpactInterest}
        />
        <FormField
          id="technicalRequirements"
          label="Technical Requirements"
          type="textarea"
          value={formData.technicalRequirements || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Technical Requirements"
          error={errors.technicalRequirements}
        />
      </Card>
      <Card className="m-3">
        <div className="flex items-center gap-4 w-full">
          <div className="flex-grow">
            <FormField
              id="text"
              label="Additional Notes"
              type="textarea"
              value={noteData.text || ''}
              onChange={(e) => handleChange(e, setNoteData)}
              placeholder="Enter Additional Notes"
            />
          </div>
          <Button variant="primary" onClick={handleNoteAdd}>
            Add Note
          </Button>
        </div>
        <NotesTable
          data={notes}
          handleDelete={(note) => handleNoteDelete(note)}
          currentPage={notesCurrentPage}
          totalPages={notesTotalPages}
          setCurrentPage={setNotesCurrentPage}
          pageSize={notesPageSize}
          setPageSize={setNotesPageSize}
          isLoading={notesIsLoading}
        />
      </Card>
      <div className="flex justify-start space-x-4 ml-3">
        <Button type="submit">Save</Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default LeadForm;
